import React from 'react';

import type { TooltipPlacement } from 'antd/es/tooltip';
import classNames from 'classnames';

import { Tooltip } from '@/shared/ui/Tooltip';

import styles from './IconButton.module.css';

export type IconButtonVariant = 'icon' | 'filled' | 'outlined' | 'tonal';
export type IconButtonSize = 'small' | 'regular' | 'medium';
export type IconButtonColor = 'standard' | 'primary';

type IconButtonProps = {
  /** 버튼 내부에 표시될 아이콘 */
  children: React.ReactNode;

  /** 버튼 클릭 핸들러 */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;

  /** 버튼 variant (기본값: 'icon') */
  variant?: IconButtonVariant;

  /** 버튼 크기 (기본값: 'regular') */
  size?: IconButtonSize;

  /** 버튼 색상 (기본값: 'standard') */
  color?: IconButtonColor;

  /** 선택 상태 */
  selected?: boolean;

  /** 툴팁 텍스트 */
  tooltip?: string;

  /** 툴팁 위치 */
  tooltipPlacement?: TooltipPlacement;

  /** 비활성화 여부 */
  disabled?: boolean;

  /** 추가 CSS 클래스 */
  className?: string;

  /** 버튼 타입 */
  type?: 'button' | 'submit' | 'reset';

  /** 접근성 라벨 */
  ariaLabel?: string;
};

export const IconButton: React.FC<IconButtonProps> = ({
  children,
  onClick,
  variant = 'icon',
  size = 'small',
  color = 'standard',
  selected = false,
  tooltip,
  tooltipPlacement = 'bottom',
  disabled = false,
  className,
  type = 'button',
  ariaLabel,
}) => {
  const button = (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={classNames(styles.iconButton, className)}
      data-variant={variant}
      data-size={size}
      data-color={color}
      data-selected={selected}
      aria-label={ariaLabel || tooltip}>
      {children}
    </button>
  );

  if (tooltip) {
    return (
      <Tooltip title={tooltip} placement={tooltipPlacement} arrow={false}>
        {button}
      </Tooltip>
    );
  }

  return button;
};
